import { Stack, Typography } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { TextButton } from '@/shared/components/ui/button/text-button/text-button';
import { OverlayContainer } from '@/shared/components/ui/data-table/components/batch-actions/data-table-batch-actions.styled';
import { TDataTableOverlayProps } from '@/shared/components/ui/data-table/components/batch-actions/data-table-batch-actions.types';
import { ItemCount } from '@/shared/components/ui/data-table/components/item-count/item-count';
import { useDataTable } from '@/shared/components/ui/data-table/context/data-table-context';
import { useDataTableSelection } from '@/shared/components/ui/data-table/hooks/use-data-table-selection/use-data-table-selection';

export const DataTableBatchActions = <T,>({
  actions,
  hideResultCount,
}: TDataTableOverlayProps<T>) => {
  const { setSelectedItems, selectedItems, gridApi } = useDataTable<T>();
  const { unchecked } = useDataTableSelection();
  const { t } = useTranslation();

  // Clearing the context alone leaves ag-grid's checkboxes ticked on every row an
  // action did not remove, so an emptied selection clears the grid's too.
  const setSelection: typeof setSelectedItems = (items) => {
    if (Array.isArray(items) && !items.length) {
      gridApi?.deselectAll();
    }

    setSelectedItems(items);
  };

  if (unchecked || !actions || !actions.length) {
    if (hideResultCount) {
      return null;
    }

    return (
      <Stack direction="row" alignItems="center" gap="1.25rem">
        <ItemCount />
      </Stack>
    );
  }

  return (
    <OverlayContainer>
      <Stack direction="row" alignItems="center" gap="1.25rem">
        <Typography variant="p1">
          {t('ui:selected', { count: selectedItems.length })}
        </Typography>
        {selectedItems.length > 0 && (
          <TextButton onClick={() => setSelection([])}>
            {t('ui:clear_selection')}
          </TextButton>
        )}
      </Stack>
      <Stack direction="row" alignItems="center" gap="0.75rem">
        {actions?.(selectedItems, setSelection)}
      </Stack>
    </OverlayContainer>
  );
};
